import apiClient from './meta-client';

export interface UrlEntry {
    url: string;
    weight: number;
}

export interface ButtonInput {
    type: 'URL' | 'POSTBACK';
    text: string;
    url_base?: string;
    url_suffix_example?: string;
    /** Múltiplas URLs com peso (weighted randomization) — se presente, tem prioridade sobre url_base/url_suffix_example no clique. */
    url_entries?: UrlEntry[];
    /** Payload enviado no evento de postback */
    payload?: string;
}

export interface UtilityTemplateCreatePayload {
    page_ids: string[];
    template_name: string;
    title: string;
    subtitle: string;
    buttons: ButtonInput[];
    /** Idioma de origem do conteúdo. Vazio/ausente = auto-detect no backend. */
    source_language?: string;
    /** Provider de IA usado na tradução automática por página. Default "openai". */
    ai_provider?: string;
    force_submit?: boolean;
    idempotency_key?: string;
}

/** Imagem por upload direto OU por ID de mídia vindo da biblioteca. */
export type UtilityTemplateImageSource =
    | { image: File; image_media_id?: never }
    | { image_media_id: number; image?: never };

export interface UtilityTemplatePageResult {
    page_id: string;
    language: string;
    success: boolean;
    template_id?: number;
    meta_template_id?: string;
    status?: string;
    meta_status?: string;
    error?: string;
}

export interface UtilityTemplateCreateResponse {
    group_id: number;
    results: UtilityTemplatePageResult[];
    message: string;
}

export interface UtilityTemplate {
    id: number;
    page_id: string;
    template_name: string;
    /** FK para UtilityTemplateGroup — null nos poucos registros legados sem grupo. */
    group_id: number | null;
    language: string;
    category: string;
    title: string;
    subtitle: string;
    status: string;
    meta_status: string | null;
    meta_template_id: string | null;
    image_url?: string | null;
    utility_risk_level: 'low' | 'medium' | 'high';
    rejection_reason: string | null;
    specific_rejection_reason: string | null;
    /** Detalhe do erro quando status='failed'. meta_error vem de resposta HTTP da Meta; error/message são o fallback (exceção sem resposta da Meta, ou erro fora do fluxo de submissão que grava neste formato). */
    last_error?: {
        error?: string;
        message?: string;
        operation?: string;
        status_code?: number;
        meta_error?: {
            code?: number;
            error_subcode?: number;
            /** Mensagem legível da Meta (ex: "O nome do modelo já existe.") — priorizar sobre `message`/`error`. */
            error_user_msg?: string;
            message?: string;
            fbtrace_id?: string;
        };
    } | null;
    retry_count?: number;
    /** Payload original em português (fonte), antes de qualquer tradução. */
    original_payload?: {
        title?: string;
        subtitle?: string;
        buttons?: { text?: string }[];
    } | null;
    /**
     * O que a IA traduziu/encurtou para o idioma da página. `shortened_fields`
     * só existe quando um botão ou o header excedeu o limite de caracteres da
     * Meta após a tradução — `ai_shortened` é o texto reescrito pela IA dentro
     * do limite; `used_fallback_truncation=true` indica que a IA não resolveu
     * e o texto foi cortado diretamente (text[:N]).
     */
    translated_content?: {
        translated?: {
            title?: string;
            subtitle?: string;
            buttons?: { text?: string }[];
        };
        shortened_fields?: {
            field: string;
            original: string;
            ai_shortened?: string | null;
            used_fallback_truncation: boolean;
        }[];
        /**
         * Presente quando este registro é uma VARIANTE gerada por retry de
         * um template 'rejected' (template_name virou _v2/_v3...) — guarda o
         * vínculo com o template/nome anterior e o motivo da rejeição que
         * motivou o retry.
         */
        retry_origin?: {
            previous_template_id?: number;
            previous_meta_template_id?: string | null;
            previous_template_name?: string;
            rejection_reason_at_retry?: string | null;
            specific_rejection_reason_at_retry?: string | null;
            retried_at?: string;
        };
    } | null;
    /** Payload final exato enviado à Graph API — salvo sempre, mesmo em falha. */
    meta_payload?: {
        name?: string;
        language?: string;
        category?: string;
        components?: unknown[];
    } | null;
    created_at: string;
    updated_at: string;
}

export type UtilityTemplateStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'failed';

export interface UtilityTemplateSyncResult {
    checked: number;
    updated: number;
    errors: { template_id: number; error: string }[];
}

/**
 * Grupo = fonte única de verdade do conteúdo ORIGINAL (idioma em que o
 * usuário escreveu, antes de qualquer tradução por página). Um grupo pode
 * ter N submissões (UtilityTemplate) em N idiomas diferentes.
 */
export interface UtilityTemplateGroup {
    id: number;
    user_id: number;
    template_name: string;
    source_language: string | null;
    title: string;
    subtitle: string;
    buttons: ButtonInput[];
    image_path: string | null;
    image_url: string | null;
    thumbnail_url?: string | null;
    ai_provider: string;
    created_at: string;
    updated_at: string;
    /** Quantas páginas do grupo estão com status='approved'. */
    approved_count: number;
    /** Contagem de páginas por status, ex: {"approved": 337, "failed": 8}. */
    status_counts: Record<string, number>;
    /** internal_category_id das páginas do grupo (para o filtro por categoria). */
    category_ids: number[];
    /** IDs de categoria/pasta de TEMPLATE associadas ao grupo (livre, sem hierarquia). Distinto de `category_ids` (categoria de página). */
    utility_category_ids?: number[];
    /** Nome de quem criou — biblioteca é compartilhada entre todos os clientes. */
    owner_name: string | null;
    /** Se o usuário logado é o dono deste grupo (para o filtro "meus"). */
    is_mine: boolean;
}

export interface UtilityTemplateGroupDetail extends UtilityTemplateGroup {
    submissions: UtilityTemplate[];
}

export const getUtilityTemplateGroups = async (): Promise<UtilityTemplateGroup[]> => {
    const response = await apiClient.get<UtilityTemplateGroup[]>('/meta/utility-templates/groups');
    return response.data;
};

export const getUtilityTemplateGroupDetail = async (
    groupId: number
): Promise<UtilityTemplateGroupDetail> => {
    const response = await apiClient.get<UtilityTemplateGroupDetail>(
        `/meta/utility-templates/groups/${groupId}`
    );
    return response.data;
};

export const addPagesToGroup = async (
    groupId: number,
    pageIds: string[],
    forceSubmit = false
): Promise<UtilityTemplateCreateResponse> => {
    const response = await apiClient.post<UtilityTemplateCreateResponse>(
        `/meta/utility-templates/groups/${groupId}/pages`,
        { page_ids: pageIds, force_submit: forceSubmit }
    );
    return response.data;
};

/**
 * Retorna todos os templates de uma vez (ou de uma página, se informado).
 * Filtro por status/categoria e contadores são calculados no frontend a
 * partir dessa mesma resposta — não há round-trip extra por clique de filtro.
 */
export const getUtilityTemplates = async (pageId?: string): Promise<UtilityTemplate[]> => {
    const response = await apiClient.get<UtilityTemplate[]>('/meta/utility-templates', {
        params: pageId ? { page_id: pageId } : {},
    });
    return response.data;
};

export const syncUtilityTemplates = async (): Promise<UtilityTemplateSyncResult> => {
    const response = await apiClient.post<UtilityTemplateSyncResult>('/meta/utility-templates/sync');
    return response.data;
};

export const createUtilityTemplate = async (
    payload: UtilityTemplateCreatePayload,
    // Opcional — bloco "Texto" (complementar, só título/subtítulo) não tem
    // imagem. Backend já suporta grupo sem image_path (_ensure_group).
    imageSource?: UtilityTemplateImageSource
): Promise<UtilityTemplateCreateResponse> => {
    const formData = new FormData();
    formData.append('page_ids', JSON.stringify(payload.page_ids));
    formData.append('template_name', payload.template_name);
    formData.append('title', payload.title);
    formData.append('subtitle', payload.subtitle);
    formData.append('buttons', JSON.stringify(payload.buttons));
    formData.append('ai_provider', payload.ai_provider ?? 'openai');
    if (payload.source_language) {
        formData.append('source_language', payload.source_language);
    }
    if (payload.force_submit !== undefined) {
        formData.append('force_submit', String(payload.force_submit));
    }
    if (payload.idempotency_key) {
        formData.append('idempotency_key', payload.idempotency_key);
    }
    if (imageSource && 'image' in imageSource && imageSource.image) {
        formData.append('image', imageSource.image);
    } else if (imageSource && 'image_media_id' in imageSource && imageSource.image_media_id) {
        formData.append('image_media_id', String(imageSource.image_media_id));
    }

    const response = await apiClient.post<UtilityTemplateCreateResponse>(
        '/meta/utility-templates',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
};

export const getUtilityTemplate = async (id: number): Promise<UtilityTemplate> => {
    const response = await apiClient.get<UtilityTemplate>(`/meta/utility-templates/${id}`);
    return response.data;
};

export interface UtilityTemplateRetrySkipped {
    template_id: number;
    reason: string;
}

export interface UtilityTemplateRetryResponse {
    reset_count: number;
    fired_groups: number[];
    skipped: UtilityTemplateRetrySkipped[];
}

/**
 * Reprocessa templates com status='failed'. Qualquer outro status é pulado
 * e reportado em `skipped`, sem erro.
 */
export const retryUtilityTemplates = async (
    templateIds: number[]
): Promise<UtilityTemplateRetryResponse> => {
    const response = await apiClient.post<UtilityTemplateRetryResponse>(
        '/meta/utility-templates/retry',
        { template_ids: templateIds }
    );
    return response.data;
};

// ---------------------------------------------------------------------
// Comparação com a Meta (templates órfãos)
// ---------------------------------------------------------------------
// Identifica templates cadastrados diretamente na conta da Meta da página
// que o nosso sistema não conhece (criados fora do fluxo normal, ou de
// testes antigos) — podem estar causando o erro Meta código 10 ("This page
// is restricted from creating templates") por ocupar nome/limite na conta.

export interface MetaTemplateListItem {
    id: string;
    name: string;
    status: string;
    category?: string;
    language?: string;
    /** true se existe um registro correspondente (mesmo meta_template_id) em meta_utility_templates. */
    known_to_system: boolean;
}

export interface MetaTemplateListResponse {
    success: boolean;
    page_id: string;
    total: number;
    orphan_count: number;
    templates: MetaTemplateListItem[];
}

export const getMetaTemplatesForPage = async (pageId: string): Promise<MetaTemplateListResponse> => {
    const response = await apiClient.get<MetaTemplateListResponse>(
        `/meta/utility-templates/pages/${pageId}/meta-templates`
    );
    return response.data;
};

export interface DeleteMetaTemplateParams {
    meta_template_id?: string;
    template_name?: string;
}

export interface DeleteMetaTemplateResponse {
    success: boolean;
    meta_template_id: string;
    local_template_id: number | null;
}

/**
 * Exclui um template diretamente na conta REAL da Meta da página.
 * AÇÃO IRREVERSÍVEL — não há undo na Meta.
 */
export const deleteMetaTemplate = async (
    pageId: string,
    params: DeleteMetaTemplateParams
): Promise<DeleteMetaTemplateResponse> => {
    const response = await apiClient.delete<DeleteMetaTemplateResponse>(
        `/meta/utility-templates/pages/${pageId}/meta-templates`,
        { params }
    );
    return response.data;
};
